import { useEffect, useState } from 'react';

/**
 * Custom React hook to track if the screen width is within a specified range.
 * Useful for implementing responsive behavior in components.
 *
 * @param minWidth - Optional minimum screen width to match.
 * @param maxWidth - Optional maximum screen width to match.
 * @returns `true` if the screen width is within the range, otherwise `false`.
 */
function useResponsive(minWidth?: number, maxWidth?: number): boolean {
  const getMatch = () => {
    if (typeof window === 'undefined') return false;
    const width = window.innerWidth;
    return (
      (minWidth === undefined || width >= minWidth) &&
      (maxWidth === undefined || width <= maxWidth)
    );
  };

  const [isMatch, setIsMatch] = useState(getMatch);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setIsMatch(getMatch());
    };

    window.addEventListener('resize', handleResize);
    handleResize(); // Check on mount

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [minWidth, maxWidth]);

  return isMatch;
}

export default useResponsive;
